import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link,useNavigate } from 'react-router-dom';
import useToggleSidebar from "../../../components/togglesidebar";
import Sidebar from '../../sidebar';
import Header from '../../Header';
import Footer from '../../footer';
import axios from 'axios';
import API_URL, { buildImageUrl } from "../../../config/config";
import '../../../styles/challengerequests.css';

const isValidObjectId = (v) => typeof v === "string" && /^[a-fA-F0-9]{24}$/.test(v.trim());

function AddProvider() {
    const navigate = useNavigate();
    const token = localStorage.getItem('adminToken');
    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

    const formatFileSize = (bytes) => {
        const n = Number(bytes) || 0;
        if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}MB`;
        return `${Math.ceil(n / 1024)}KB`;
    };

    const [formData, setFormData] = useState({
        name: '',
        url: '',
        website: '',
        metaKeywords: '',
        location: '',
        verified: '',
        rating: '',
        topSellers: '',
        coverImage: '',
        icon: '',
        facebook: '',
        instagram: '',
        pinterest: '',
        twitter: '',
        youtube: ''
    });

    const [countries, setCountries] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [features, setFeatures] = useState([]);
    const [fabrics, setFabrics] = useState([]);
    const [categories, setCategories] = useState([]);

    // Social media links (editable list) and helper platforms
    const [socialMediaLinks, setSocialMediaLinks] = useState([{ id: '1', platform: '', url: '' }]);
    const socialMediaPlatforms = [
        { value: 'instagram', label: 'Instagram' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'twitter', label: 'Twitter' },
        { value: 'youtube', label: 'YouTube' },
        { value: 'telegram', label: 'Telegram' }
    ];

    const [selectedHighlights, setSelectedHighlights] = useState([]);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [selectedFabrics, setSelectedFabrics] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    const [imagePreviews, setImagePreviews] = useState({ coverImageUrl: null, iconUrl: null });
    const [selectedUploadInfo, setSelectedUploadInfo] = useState({ coverImage: null, icon: null });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            try {
                const baseApi = API_URL.replace('/admin', '');
                const [countriesRes, highlightsRes, featuresRes, fabricsRes, categoriesRes] = await Promise.all([
                    axios.get(`${baseApi}/countries`),
                    axios.get(`${baseApi}/highlights`),
                    axios.get(`${baseApi}/features`),
                    axios.get(`${baseApi}/fabrics`),
                    axios.get(`${baseApi}/categories`),
                ]);

                setCountries(countriesRes.data.countries || countriesRes.data || []);
                setHighlights(highlightsRes.data.data || highlightsRes.data || []);
                setFeatures(featuresRes.data.features || featuresRes.data || []);
                setFabrics(fabricsRes.data.fabrics || fabricsRes.data || []);
                setCategories(categoriesRes.data.categories || categoriesRes.data || []);
            } catch (err) {
                console.error('Failed to load options', err);
            }
        };
        fetch();
    }, []);

    const handleInput = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSocialMediaChange = (id, field, value) => {
        setSocialMediaLinks(prev => prev.map(link => link.id === id ? { ...link, [field]: value } : link));
    };

    const addSocialMediaLink = () => {
        if (socialMediaLinks.length < 5) {
            setSocialMediaLinks(prev => [...prev, { id: Date.now().toString(), platform: '', url: '' }]);
        }
    };

    const removeSocialMediaLink = (id) => {
        setSocialMediaLinks(prev => prev.filter(s => s.id !== id));
    };

    const triggerFileInput = (id) => {
        const el = document.getElementById(id);
        el && el.click();
    }

    const handleFileChange = async (field, file) => {
        if (!file) return;

        setSelectedUploadInfo((prev) => ({
            ...prev,
            [field]: { name: file.name, size: formatFileSize(file.size) }
        }));

        const previewUrl = URL.createObjectURL(file);
        setImagePreviews(prev => ({ ...prev, [field === 'coverImage' ? 'coverImageUrl' : 'iconUrl']: previewUrl }));

        try {
            const form = new FormData();
            form.append(field, file);
            const baseApi = API_URL.replace('/admin', '');
            const res = await axios.post(`${baseApi}/providers/upload-image`, form, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });
            // server returns paths like /uploads/..., construct full url using buildImageUrl
            const url = (res.data && (res.data[field] || res.data.fileUrl)) ? buildImageUrl(res.data[field] || res.data.fileUrl) : previewUrl;
            setFormData(prev => ({ ...prev, [field]: url }));
            setImagePreviews(prev => ({ ...prev, [field === 'coverImage' ? 'coverImageUrl' : 'iconUrl']: url }));
        } catch (err) {
            console.error('Upload failed', err);
            alert(err?.response?.data?.message || err?.response?.data?.error || 'Image upload failed.');
            setFormData(prev => ({ ...prev, [field]: previewUrl }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = [];
        const name = (formData.name || '').trim();
        const url = (formData.url || '').trim();
        const location = (formData.location || '').trim();

        if (!name) errors.push('Company name is required');
        if (!url) errors.push('Company store link (URL) is required');
        if (!location) errors.push('Country is required');

        const ratingRaw = formData.rating;
        const ratingNum = ratingRaw === '' ? 0 : Number(ratingRaw);
        if (!Number.isFinite(ratingNum) || ratingNum < 0 || ratingNum > 5) {
            errors.push('Rating must be between 0 and 5');
        }

        if (errors.length) {
            alert(errors.join('. '));
            return;
        }

        setSubmitting(true);
        try {
            const baseApi = API_URL.replace('/admin', '');

            const normalizeIds = (arr) => (Array.isArray(arr) ? arr.map(String).map((s) => s.trim()).filter(isValidObjectId) : []);

            const socialMedia = (Array.isArray(socialMediaLinks) ? socialMediaLinks : [])
                .map((x) => {
                    const platform = (x?.platform || '').toString().trim();
                    const url = (x?.url || '').toString().trim();
                    return platform && url ? { platform, url } : null;
                })
                .filter(Boolean);

            const topSellers = (formData.topSellers || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((name) => ({ name }));

            const payload = {
                name,
                url,
                website: formData.website,
                metaKeywords: formData.metaKeywords,
                location,
                isVerified: formData.verified,
                highlights: normalizeIds(selectedHighlights),
                features: normalizeIds(selectedFeatures),
                fabrics: normalizeIds(selectedFabrics),
                categories: normalizeIds(selectedCategories),
                coverImage: formData.coverImage,
                icon: formData.icon,
                socialMedia,
                topSellers,
                rating: ratingNum
            };

            await axios.post(`${baseApi}/providers`, payload, { headers: { Authorization: `Bearer ${token}` } });
            navigate('/admin/providers', { state: { type: 'success', message: 'Provider added successfully' } });
        } catch (err) {
            console.error('Create failed', err);
            alert(err.response?.data?.message || err.response?.data?.error || 'Failed to create provider');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet><title>Admin | Add Provider</title></Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? 'blurred' : ''}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? '250px' : '70px', transition: 'margin-left .3s' }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <section className='content'>
                        <div className="breadcrumb-main-bx">
                            <div className="breadcrumb-bx">
                                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <div className="breadcrumb-link breadcrumb-active">Add Provider</div>
                            </div>
                        </div>

                        <div className='container-fluid'>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <div className='card'>
                                        <div className='card-header'>Add Provider</div>
                                        <div className='card-body'>
                                            <form onSubmit={handleSubmit}>
                                                <div className='mb-3'>
                                                    <label className='form-label'>Company Name</label>
                                                    <input className='form-control' value={formData.name} onChange={(e)=>handleInput('name', e.target.value)} />
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Country</label>
                                                    <select className='form-control' value={formData.location} onChange={(e) => handleInput('location', e.target.value)}>
                                                        <option value=''>Select Country</option>
                                                        {countries.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                                    </select>
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Verified</label>
                                                    <div>
                                                        <label className='me-3'><input type='radio' name='verified' value='yes' checked={formData.verified==='yes'} onChange={(e)=>handleInput('verified', e.target.value)} /> Yes</label>
                                                        <label><input type='radio' name='verified' value='no' checked={formData.verified==='no'} onChange={(e)=>handleInput('verified', e.target.value)} /> No</label>
                                                    </div>
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Cover Image</label>
                                                    <div style={{ border: '1px dashed #ddd', padding: 12, borderRadius: 6 }} onClick={()=>triggerFileInput('cover-input')}> 
                                                        {imagePreviews.coverImageUrl ? <img src={imagePreviews.coverImageUrl} alt='cover' style={{ maxWidth: '100%', maxHeight: 160 }} /> : <div style={{ color:'#666' }}>
                                                            <div style={{ fontWeight: 600 }}>Click to upload</div>
                                                            <div style={{ fontSize: 12 }}>Max 500KB</div>
                                                        </div>}
                                                    </div>
                                                    <div className="mt-1" style={{ fontSize: 12, color: '#6c757d' }}>
                                                        Max 500KB{selectedUploadInfo.coverImage ? ` | Selected: ${selectedUploadInfo.coverImage.size}` : ''}
                                                    </div>
                                                    <input id='cover-input' type='file' accept='image/*' style={{ display:'none' }} onChange={(e)=>handleFileChange('coverImage', e.target.files?.[0])} />
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Icon</label>
                                                    <div style={{ border: '1px dashed #ddd', padding: 12, borderRadius: 6 }} onClick={()=>triggerFileInput('icon-input')}> 
                                                        {imagePreviews.iconUrl ? <img src={imagePreviews.iconUrl} alt='icon' style={{ maxWidth: '120px', maxHeight: 120 }} /> : <div style={{ color:'#666' }}>
                                                            <div style={{ fontWeight: 600 }}>Click to upload</div>
                                                            <div style={{ fontSize: 12 }}>Max 500KB</div>
                                                        </div>}
                                                    </div>
                                                    <div className="mt-1" style={{ fontSize: 12, color: '#6c757d' }}>
                                                        Max 500KB{selectedUploadInfo.icon ? ` | Selected: ${selectedUploadInfo.icon.size}` : ''}
                                                    </div>
                                                    <input id='icon-input' type='file' accept='image/*' style={{ display:'none' }} onChange={(e)=>handleFileChange('icon', e.target.files?.[0])} />
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Company Store Link</label>
                                                    <input className='form-control' value={formData.url || ''} onChange={(e)=>handleInput('url', e.target.value)} placeholder='https://provider.com' />
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Website</label>
                                                    <input className='form-control' value={formData.website} onChange={(e)=>handleInput('website', e.target.value)} />
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Meta Keywords (comma separated)</label>
                                                    <input className='form-control' value={formData.metaKeywords || ''} onChange={(e)=>handleInput('metaKeywords', e.target.value)} placeholder='medical scrubs, uniforms, custom apparel' />
                                                </div>

                                                {/* Social Media Links (editable list) */}
                                                <div className='mb-3'>
                                                    <label className='form-label'>Social Media Links</label>
                                                    <div>
                                                        {socialMediaLinks.map(link => (
                                                            <div key={link.id} className='d-flex gap-2 align-items-center mb-2'>
                                                                <select className='form-select' value={link.platform} onChange={(e)=>handleSocialMediaChange(link.id, 'platform', e.target.value)} style={{width:160}}>
                                                                    <option value=''>Platform</option>
                                                                    {socialMediaPlatforms.map(p=> <option key={p.value} value={p.value}>{p.label}</option>)}
                                                                </select>
                                                                <input className='form-control' placeholder='https://...' value={link.url} onChange={(e)=>handleSocialMediaChange(link.id, 'url', e.target.value)} />
                                                                <button type='button' className='btn btn-outline-danger' onClick={()=>removeSocialMediaLink(link.id)}>Remove</button>
                                                            </div>
                                                        ))}
                                                        <div>
                                                            <button type='button' className='btn btn-sm btn-secondary' onClick={addSocialMediaLink}>Add Link</button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Top Sellers (comma separated)</label>
                                                    <input className='form-control' value={formData.topSellers || ''} onChange={(e)=>handleInput('topSellers', e.target.value)} />
                                                </div>

                                                {/* Highlights, Features, Fabrics, Categories checkboxes */}
                                                <div className='mb-3'>
                                                    <label className='form-label'>Key Highlights</label>
                                                    <div className='d-flex flex-wrap gap-2'>
                                                        {highlights.map(h => (
                                                            <div key={h._id || h} className='form-check'>
                                                                <input className='form-check-input' type='checkbox' id={`hl-${h._id || h}`} checked={selectedHighlights.includes(h._id || h)} onChange={() => {
                                                                    setSelectedHighlights(prev => prev.includes(h._id || h) ? prev.filter(x=>x!== (h._id||h)) : [...prev, (h._id||h)])
                                                                }} />
                                                                <label className='form-check-label' htmlFor={`hl-${h._id || h}`}>{h.name || h}</label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Features</label>
                                                    <div className='d-flex flex-wrap gap-2'>
                                                        {features.map(f => (
                                                            <div key={f._id || f} className='form-check'>
                                                                <input className='form-check-input' type='checkbox' id={`ft-${f._id || f}`} checked={selectedFeatures.includes(f._id || f)} onChange={() => {
                                                                    setSelectedFeatures(prev => prev.includes(f._id || f) ? prev.filter(x=>x!== (f._id||f)) : [...prev, (f._id||f)])
                                                                }} />
                                                                <label className='form-check-label' htmlFor={`ft-${f._id || f}`}>{f.name || f}</label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Fabrics</label>
                                                    <div className='d-flex flex-wrap gap-2'>
                                                        {fabrics.map(f => (
                                                            <div key={f._id || f} className='form-check'>
                                                                <input className='form-check-input' type='checkbox' id={`fb-${f._id || f}`} checked={selectedFabrics.includes(f._id || f)} onChange={() => {
                                                                    setSelectedFabrics(prev => prev.includes(f._id || f) ? prev.filter(x=>x!== (f._id||f)) : [...prev, (f._id||f)])
                                                                }} />
                                                                <label className='form-check-label' htmlFor={`fb-${f._id || f}`}>{f.name || f}</label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className='mb-3'>
                                                    <label className='form-label'>Categories</label>
                                                    <div className='d-flex flex-wrap gap-2'>
                                                        {categories.map(c => (
                                                            <div key={c._id || c} className='form-check'>
                                                                <input className='form-check-input' type='checkbox' id={`cat-${c._id || c}`} checked={selectedCategories.includes(c._id || c)} onChange={() => {
                                                                    setSelectedCategories(prev => prev.includes(c._id || c) ? prev.filter(x=>x!== (c._id||c)) : [...prev, (c._id||c)])
                                                                }} />
                                                                <label className='form-check-label' htmlFor={`cat-${c._id || c}`}>{c.name || c}</label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* <div className='mb-3'>
                                                    <label className='form-label'>Initial Rating</label>
                                                    <input className='form-control' value={formData.rating} onChange={(e)=>handleRatingChange(e.target.value)} placeholder='4.5' />
                                                    <small className='text-muted'>Rating from 0 to 5 (max 1 decimal place)</small>
                                                </div> */}

                                                <div className='d-flex gap-2'>
                                                    <button className='btn btn-secondary' type='button' onClick={()=>navigate('/admin/providers')}>Cancel</button>
                                                    <button className='btn btn-primary' type='submit' disabled={submitting}>{submitting ? 'Creating...' : 'Add Provider'}</button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>
        </>
    );
}

export default AddProvider;
